/**
 * The Statistics popup (STA-002, owner 2026-09-25): Wordle's four numbers, the result
 * chart with today's bar highlighted, average against par, and the countdown to the
 * next table. Opened from the top bar and from the results screen.
 *
 * Recomputed every time it opens (stats/stats.ts), because a shot may have been saved
 * since. The countdown ticks once a second only while it is open, on the server's
 * clock as this page knows it, never the device's (D-027).
 *
 * The same native `<dialog>` and styling as Settings and How to play.
 */
import { STATS_COPY } from '../config/site.ts';
import { formatAverage, formatCountdown, type Stats } from '../stats/stats.ts';

export interface Countdown {
  /** When the next table (or, before launch, the first) arrives, in server time. */
  readonly targetMs: number;
  /** The server's clock, as this page knows it. */
  now(): number;
  /** Before the daily game has started, it counts to the first table. */
  readonly first: boolean;
}

export interface StatsDialog {
  open(): void;
}

export function createStatsDialog(
  host: HTMLElement,
  stats: () => Stats,
  countdown: Countdown | undefined,
  /** ACC-011: whether the numbers are the account's (signed in) or this device's. */
  fromAccount: () => boolean = () => false,
): StatsDialog {
  const c = STATS_COPY;
  const dialog = document.createElement('dialog');
  dialog.className = 'settings stats';
  dialog.setAttribute('aria-labelledby', 'stats-title');
  dialog.innerHTML = `
    <form method="dialog" class="settings__card stats__card">
      <header class="settings__header">
        <h2 class="settings__title" id="stats-title">${c.title}</h2>
        <button type="submit" class="btn btn--icon" aria-label="${c.close}">&times;</button>
      </header>
      <dl class="stats__numbers">
        <div><dt>${c.played}</dt><dd class="numeral" data-stat="played"></dd></div>
        <div><dt>${c.cleared}</dt><dd class="numeral" data-stat="cleared"></dd></div>
        <div><dt>${c.currentStreak}</dt><dd class="numeral" data-stat="current"></dd></div>
        <div><dt>${c.bestStreak}</dt><dd class="numeral" data-stat="best"></dd></div>
      </dl>
      <p class="stats__average">${c.average}: <strong class="numeral" data-stat="average"></strong></p>
      <section class="stats__section">
        <h3 class="how-to-play__heading">${c.results}</h3>
        <ol class="stats__bars" data-bars></ol>
      </section>
      <section class="stats__section stats__next" data-next hidden>
        <p class="stats__next-label" data-next-label></p>
        <p class="stats__next-time numeral" data-next-time role="timer"></p>
        <button type="button" class="btn btn--primary" data-play hidden>${c.playIt}</button>
      </section>
      <p class="stats__note" data-note></p>
    </form>
  `;
  host.append(dialog);
  const note = dialog.querySelector<HTMLParagraphElement>('[data-note]');

  const field = (name: string): HTMLElement => {
    const found = dialog.querySelector<HTMLElement>(`[data-stat="${name}"]`);
    if (found === null) throw new Error(`stats dialog: ${name} is missing`);
    return found;
  };
  const need = <T extends HTMLElement>(selector: string): T => {
    const found = dialog.querySelector<T>(selector);
    if (found === null) throw new Error(`stats dialog: ${selector} is missing`);
    return found;
  };
  const bars = need<HTMLOListElement>('[data-bars]');
  const next = need<HTMLElement>('[data-next]');
  const nextLabel = need<HTMLParagraphElement>('[data-next-label]');
  const nextTime = need<HTMLParagraphElement>('[data-next-time]');
  const play = need<HTMLButtonElement>('[data-play]');

  play.addEventListener('click', () => {
    window.location.reload();
  });
  // A press on the backdrop closes it, like the other popups.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  let ticker: number | undefined;
  const stopTicking = (): void => {
    if (ticker !== undefined) window.clearInterval(ticker);
    ticker = undefined;
  };
  dialog.addEventListener('close', stopTicking);

  function renderCountdown(): void {
    if (countdown === undefined) return;
    const left = countdown.targetMs - countdown.now();
    const ready = left <= 0;
    nextLabel.textContent = ready ? c.ready : countdown.first ? c.firstTable : c.nextTable;
    nextTime.hidden = ready;
    nextTime.textContent = formatCountdown(left);
    play.hidden = !ready;
    if (ready) stopTicking();
  }

  function render(): void {
    const s = stats();
    field('played').textContent = String(s.played);
    field('cleared').textContent = String(s.clearedPercent);
    field('current').textContent = String(s.currentStreak);
    field('best').textContent = String(s.bestStreak);
    field('average').textContent =
      s.averageVsPar === undefined ? c.noAverage : formatAverage(s.averageVsPar, c.level);

    // Bars scale to the biggest count, so the chart has shape from the first day.
    const most = Math.max(1, ...s.results.map((bar) => bar.count));
    bars.replaceChildren(
      ...s.results.map((bar) => {
        const item = document.createElement('li');
        item.className = 'stats__bar';
        item.classList.toggle('stats__bar--today', bar.label === s.todayLabel);
        const label = document.createElement('span');
        label.className = 'stats__bar-label';
        label.textContent = bar.label;
        const track = document.createElement('span');
        track.className = 'stats__bar-track';
        const fill = document.createElement('span');
        fill.className = 'stats__bar-fill numeral';
        // CSSOM, not a style attribute: the strict CSP forbids inline styles.
        fill.style.setProperty('--share', String(bar.count / most));
        fill.textContent = String(bar.count);
        track.append(fill);
        item.append(label, track);
        return item;
      }),
    );

    next.hidden = countdown === undefined;
    renderCountdown();
  }

  return {
    open() {
      render();
      if (note !== null) note.textContent = fromAccount() ? c.accountNote : c.deviceNote;
      stopTicking();
      if (countdown !== undefined) ticker = window.setInterval(renderCountdown, 1000);
      dialog.showModal();
    },
  };
}
